import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { ConnectionState, NewPDFMessage } from './types.js';
import { getAccessToken, triggerReauth } from './auth.js';

export interface ContainerSignalRConfig {
  containerId: string;
  containerName: string;
  hubUrl: string;
}

export class ContainerSignalRClient {
  private connection: HubConnection | null = null;
  private isManualDisconnect = false;
  private isInitialConnection = true;
  private isConnecting = false;
  private eventListeners: Map<string, Function[]> = new Map();
  private config: ContainerSignalRConfig;

  constructor(config: ContainerSignalRConfig) {
    this.config = config;
  }

  async connect(): Promise<void> {
    if (this.connection?.state === 'Connected') {
      console.log(`SignalR already connected for ${this.config.containerName}, skipping...`);
      return;
    }

    if (this.connection?.state === 'Connecting' || this.connection?.state === 'Reconnecting') {
      console.log(`SignalR connection already active (${this.connection.state}) for ${this.config.containerName}, skipping...`);
      return;
    }

    if (this.isConnecting) {
      console.log(`SignalR connection already in progress for ${this.config.containerName}, skipping...`);
      return;
    }

    console.log(`Starting SignalR connection for ${this.config.containerName}...`);
    this.isManualDisconnect = false;
    this.isInitialConnection = true;
    this.isConnecting = true;
    this.updateConnectionState('connecting');

    try {
      this.connection = new HubConnectionBuilder()
        .withUrl(this.config.hubUrl, {
          accessTokenFactory: async () => {
            try {
              const token = await getAccessToken();
              return token || '';
            } catch {
              await triggerReauth();
              return '';
            }
          }
        })
        .withAutomaticReconnect([2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Information)
        .build();

      // Set up event handlers
      this.setupEventHandlers();

      // Start connection
      console.log(`Starting SignalR connection for ${this.config.containerName}...`);
      await this.connection.start();
      
      // Wait a moment to ensure connection is stable
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Mark initial connection as complete
      this.isInitialConnection = false;
      this.isConnecting = false;
      this.updateConnectionState('connected');
      
      console.log(`SignalR connected for ${this.config.containerName} (${this.config.containerId})`);
    } catch (error) {
      console.error(`SignalR connection failed for ${this.config.containerName}:`, error);
      this.isInitialConnection = false;
      this.isConnecting = false;
      this.updateConnectionState('disconnected');
      this.connection = null;
    }
  }

  private setupEventHandlers(): void {
    if (!this.connection) return;

    console.log(`Setting up SignalR event handlers for ${this.config.containerName}...`);

    // Connection state change handlers
    this.connection.onreconnecting(() => {
      // Don't update state during initial connection
      if (this.isInitialConnection) {
        console.log(`SignalR reconnecting during initial connection for ${this.config.containerName}, ignoring...`);
        return;
      }
      console.log(`SignalR reconnecting for ${this.config.containerName}...`);
      this.updateConnectionState('reconnecting');
    });

    this.connection.onreconnected(() => {
      // Don't update state during initial connection
      if (this.isInitialConnection) {
        console.log(`SignalR reconnected during initial connection for ${this.config.containerName}, ignoring...`);
        return;
      }
      console.log(`SignalR reconnected for ${this.config.containerName}...`);
      this.updateConnectionState('connected');
    });

    this.connection.onclose((error) => {
      if (error) {
        console.error(`SignalR connection error for ${this.config.containerName}:`, error);
      }

      if (this.isManualDisconnect) {
        console.log(`Manual disconnect for ${this.config.containerName}, setting to disconnected`);
      } else {
        console.log(`SignalR closed for ${this.config.containerName} after reconnect attempts; setting disconnected state`);
      }

      this.isConnecting = false;
      this.updateConnectionState('disconnected');
    });

    // Custom message handlers - filter by container
    this.connection.on('newPdfUploaded', (message: NewPDFMessage | NewPDFMessage[]) => {
      const messages = Array.isArray(message) ? message : [message];

      for (const currentMessage of messages) {
        if (!currentMessage) {
          continue;
        }

        console.log(`New PDF received for ${this.config.containerName}:`, currentMessage);

        // Only process PDFs for this specific container
        if (currentMessage.containerName === this.config.containerId) {
          console.log(`Processing PDF for ${this.config.containerName}: ${currentMessage.fileName}`);
          this.emit('newPDF', currentMessage);
        } else {
          console.log(`Ignoring PDF for different container: ${currentMessage.containerName} (expected: ${this.config.containerId})`);
        }
      }
    });
  }


  private updateConnectionState(state: ConnectionState): void {
    console.log(`Connection state changed to: ${state} for ${this.config.containerName}`);
    this.emit('connectionChange', state);
  }

  public async disconnect(): Promise<void> {
    this.isManualDisconnect = true;
    
    if (this.connection) {
      try {
        await this.connection.stop();
        console.log(`SignalR disconnected for ${this.config.containerName}`);
      } catch (error) {
        console.error(`Error disconnecting SignalR for ${this.config.containerName}:`, error);
      }
      this.connection = null;
    }
  }

  public getConnectionState(): ConnectionState {
    if (!this.connection) return 'disconnected';
    
    // Convert HubConnectionState to our ConnectionState type
    switch (this.connection.state) {
      case 'Connected':
        return 'connected';
      case 'Connecting':
        return 'connecting';
      case 'Reconnecting':
        return 'reconnecting';
      case 'Disconnected':
        return 'disconnected';
      default:
        return 'disconnected';
    }
  }

  public on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  public off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(data);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  public isConnected(): boolean {
    return this.connection?.state === 'Connected';
  }

  public getContainerInfo(): { id: string; name: string } {
    return {
      id: this.config.containerId,
      name: this.config.containerName
    };
  }
}
